import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Loader2, Ban, ArrowLeft, Search, Users, Plane, MousePointerClick, TrendingUp, AlertTriangle, BadgeCheck } from 'lucide-react';
import { fmtDateISO, fmtDateTime } from '../lib/dateFormat';

const useAdminGuard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [denied, setDenied] = useState(false);
  useEffect(() => { if (!loading && !user) navigate('/login'); }, [user, loading, navigate]);
  return { user, loading, denied, setDenied };
};

const Shell = ({ title, subtitle, icon: Icon, children }) => {
  const navigate = useNavigate();
  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/admin')} className="text-sm text-muted-foreground hover:text-accent flex items-center gap-1 mb-3" data-testid="admin-back"><ArrowLeft className="h-4 w-4" /> Back to dashboard</button>
        <div className="flex items-center gap-2 mb-1">{Icon && <Icon className="h-6 w-6 text-accent" />}<h1 className="font-display text-2xl font-semibold">{title}</h1></div>
        {subtitle && <p className="text-sm text-muted-foreground mb-6">{subtitle}</p>}
        {children}
      </div>
    </Layout>
  );
};

const Denied = () => <Layout><div className="max-w-lg mx-auto py-24 text-center"><Ban className="h-10 w-10 mx-auto mb-2 text-amber-500" /><h1 className="font-display text-2xl font-semibold">Admin access required</h1></div></Layout>;
const Spinner = () => <div className="grid place-items-center py-24"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
const fmtDate = (d) => d ? fmtDateTime(d) : '—';

// ---------------- Users ----------------
export function AdminUsers() {
  const { user, loading, denied, setDenied } = useAdminGuard();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { document.title = 'Admin · Users | FlightDeals.in'; }, []);
  useEffect(() => { if (!user) return; const t = setTimeout(() => { api.adminUsers(q).then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); }, 250); return () => clearTimeout(t); }, [user, q, setDenied]);
  if (loading) return <Layout><Spinner /></Layout>;
  if (denied) return <Denied />;
  return (
    <Shell title="Users" subtitle="All registered users and their activity." icon={Users}>
      <div className="relative max-w-sm mb-4"><Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="pl-9" data-testid="admin-users-search" /></div>
      <Card className="p-0 rounded-2xl overflow-hidden">
        {!data ? <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 rounded-lg" />)}</div> : (
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">User</th><th>Type</th><th>Creator</th><th>Saved</th><th>Clicks</th><th>Joined</th></tr></thead>
            <tbody>{data.users.map((u) => (
              <tr key={u.user_id} className="border-b border-border last:border-0 hover:bg-muted/40" data-testid={`user-row-${u.user_id}`}>
                <td className="py-2.5 px-4"><p className="font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p></td>
                <td>{u.is_admin ? <Badge className="text-[10px] bg-accent/15 text-accent">admin</Badge> : <Badge variant="outline" className="text-[10px]">{u.auth_provider}</Badge>}</td>
                <td>{u.is_creator ? <span className="text-xs inline-flex items-center gap-1">@{u.creator_username} <Badge variant="outline" className="text-[10px]">{u.creator_status}</Badge></span> : <span className="text-xs text-muted-foreground">—</span>}</td>
                <td className="tabular">{u.saved_items}</td><td className="tabular">{u.affiliate_clicks}</td>
                <td className="text-xs text-muted-foreground">{u.created_at ? fmtDateISO(u.created_at) : '—'}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
        {data && data.users.length === 0 && <p className="p-6 text-sm text-muted-foreground">No users found.</p>}
      </Card>
    </Shell>
  );
}

// ---------------- Searches ----------------
export function AdminSearches() {
  const { user, loading, denied, setDenied } = useAdminGuard();
  const [sp, setSp] = useSearchParams();
  const type = sp.get('type') || '';
  const [data, setData] = useState(null);
  useEffect(() => { document.title = 'Admin · Searches | FlightDeals.in'; }, []);
  useEffect(() => { if (!user) return; setData(null); api.adminSearches(type).then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); }, [user, type, setDenied]);
  if (loading) return <Layout><Spinner /></Layout>;
  if (denied) return <Denied />;
  const TCOL = { flight: 'bg-sky-500/15 text-sky-700', hotel: 'bg-emerald-500/15 text-emerald-700', experience: 'bg-amber-500/15 text-amber-700' };
  return (
    <Shell title="Search logs" subtitle="Flight, hotel and experience searches." icon={Plane}>
      <Tabs value={type || 'all'} onValueChange={(v) => setSp(v === 'all' ? {} : { type: v })} className="mb-4">
        <TabsList><TabsTrigger value="all" data-testid="search-tab-all">All</TabsTrigger><TabsTrigger value="flight" data-testid="search-tab-flight">Flights</TabsTrigger><TabsTrigger value="hotel">Hotels</TabsTrigger><TabsTrigger value="experience">Experiences</TabsTrigger></TabsList>
      </Tabs>
      <Card className="p-0 rounded-2xl overflow-hidden">
        {!data ? <div className="p-6 space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-9 rounded-lg" />)}</div> : (
          <table className="w-full text-sm" data-testid="admin-searches-table">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">Type</th><th>Detail</th><th>When</th></tr></thead>
            <tbody>{data.searches.map((s, i) => (
              <tr key={i} className="border-b border-border last:border-0"><td className="py-2.5 px-4"><Badge className={`text-[10px] ${TCOL[s.type]||''}`}>{s.type}</Badge></td><td>{s.detail}</td><td className="text-xs text-muted-foreground">{fmtDate(s.created_at)}</td></tr>
            ))}</tbody>
          </table>
        )}
        {data && data.searches.length === 0 && <p className="p-6 text-sm text-muted-foreground">No searches recorded.</p>}
      </Card>
    </Shell>
  );
}

// ---------------- Conversions (also affiliate clicks) ----------------
export function AdminConversions() {
  const { user, loading, denied, setDenied } = useAdminGuard();
  const [convs, setConvs] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [tab, setTab] = useState('conversions');
  useEffect(() => { document.title = 'Admin · Conversions | FlightDeals.in'; }, []);
  useEffect(() => { if (!user) return; api.adminConversions().then(({ data }) => setConvs(data.conversions)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); api.adminClicks(100).then(({ data }) => setClicks(data.clicks)).catch(() => {}); }, [user, setDenied]);
  if (loading) return <Layout><Spinner /></Layout>;
  if (denied) return <Denied />;
  const CS = { confirmed: 'bg-emerald-500/15 text-emerald-700', pending: 'bg-amber-500/15 text-amber-700', rejected: 'bg-destructive/15 text-destructive', cancelled: 'bg-muted' };
  return (
    <Shell title="Conversions & clicks" subtitle="Affiliate attribution chain: partner → campaign → creator → itinerary → click → conversion." icon={MousePointerClick}>
      <Tabs value={tab} onValueChange={setTab} className="mb-4"><TabsList><TabsTrigger value="conversions" data-testid="conv-tab-conversions">Conversions</TabsTrigger><TabsTrigger value="clicks" data-testid="conv-tab-clicks">Affiliate clicks</TabsTrigger></TabsList></Tabs>
      {tab === 'conversions' ? (
        <Card className="p-0 rounded-2xl overflow-hidden">
          {!convs ? <div className="p-6 space-y-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-9 rounded-lg" />)}</div> : (
            <table className="w-full text-sm" data-testid="admin-conversions-table">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">Partner</th><th>Commission</th><th>Creator</th><th>Itinerary</th><th>Status</th><th>When</th></tr></thead>
              <tbody>{convs.map((c) => (
                <tr key={c.conversion_id} className="border-b border-border last:border-0"><td className="py-2.5 px-4">{c.partner || c.partner_id}</td><td className="tabular">₹{(c.commission_amount||0).toLocaleString('en-IN')}</td><td className="font-mono text-xs">{c.creator_id ? c.creator_id.slice(0,10) : '—'}</td><td className="font-mono text-xs">{c.itinerary_id ? c.itinerary_id.slice(0,10) : '—'}</td><td><Badge className={`text-[10px] ${CS[c.conversion_status]||''}`}>{c.conversion_status}</Badge></td><td className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</td></tr>
              ))}</tbody>
            </table>
          )}
          {convs && convs.length === 0 && <p className="p-6 text-sm text-muted-foreground">No conversions yet. Partner postbacks populate this.</p>}
        </Card>
      ) : (
        <Card className="p-0 rounded-2xl overflow-hidden">
          {!clicks ? <Spinner /> : (
            <table className="w-full text-sm" data-testid="admin-clicks-table">
              <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">Type</th><th>Partner</th><th>Creator</th><th>Placement</th><th>When</th></tr></thead>
              <tbody>{clicks.map((c) => (
                <tr key={c.click_id} className="border-b border-border last:border-0"><td className="py-2.5 px-4"><Badge variant="outline" className="text-[10px]">{c.offer_type}</Badge></td><td>{c.partner_id||'—'}</td><td className="font-mono text-xs">{c.creator_id ? c.creator_id.slice(0,10) : '—'}</td><td className="text-xs">{c.placement||'—'}</td><td className="text-xs text-muted-foreground">{fmtDate(c.created_at)}</td></tr>
              ))}</tbody>
            </table>
          )}
          {clicks && clicks.length === 0 && <p className="p-6 text-sm text-muted-foreground">No affiliate clicks yet.</p>}
        </Card>
      )}
    </Shell>
  );
}

// ---------------- Earnings ----------------
export function AdminEarnings() {
  const { user, loading, denied, setDenied } = useAdminGuard();
  const [data, setData] = useState(null);
  useEffect(() => { document.title = 'Admin · Earnings | FlightDeals.in'; }, []);
  useEffect(() => { if (!user) return; api.adminEarnings().then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); }, [user, setDenied]);
  if (loading) return <Layout><Spinner /></Layout>;
  if (denied) return <Denied />;
  const s = data?.summary || {};
  const KPIS = [['Estimated commission', s.estimated_commission, 'Click projection'], ['Confirmed commission', s.confirmed_commission, 'Partner-reported'], ['Creator share', s.creator_share, 'Ledger'], ['FlightDeals share', s.flightdeals_share, 'Ledger'], ['Pending payout', s.pending_payout, 'Owed to creators'], ['Paid payout', s.paid_payout, 'Settled'], ['Campaign fees', s.campaign_fees, 'Committed'], ['Barter value', s.barter_value, 'Committed']];
  return (
    <Shell title="Earnings & attribution" subtitle="Estimated vs confirmed — no real money is moved (ledger only)." icon={TrendingUp}>
      {!data ? <div className="grid gap-3 sm:grid-cols-4">{[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div> : (
        <>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 mb-4" data-testid="admin-earnings-kpis">
            {KPIS.map(([l, v, sub]) => (<Card key={l} className="p-4 rounded-2xl"><p className="text-xs text-muted-foreground">{l}</p><p className="text-2xl font-semibold tabular">₹{(v||0).toLocaleString('en-IN')}</p><p className="text-[10px] text-muted-foreground">{sub}</p></Card>))}
          </div>
          <Card className="p-3 rounded-xl bg-muted/40 text-xs text-muted-foreground mb-4">{data.note}</Card>
          <Card className="p-5 rounded-2xl">
            <h3 className="font-semibold mb-3">Payout ledger</h3>
            {data.payouts.length === 0 ? <p className="text-sm text-muted-foreground">No payouts yet.</p> : (
              <table className="w-full text-sm" data-testid="admin-earnings-ledger"><thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-1">Creator</th><th>Gross</th><th>Creator share</th><th>FD share</th><th>Status</th></tr></thead>
              <tbody>{data.payouts.map((p) => (<tr key={p.payout_id} className="border-b border-border last:border-0"><td className="py-2 font-mono text-xs">{p.creator_id.slice(0,10)}</td><td className="tabular">₹{p.gross_commission}</td><td className="tabular font-medium">₹{p.creator_share}</td><td className="tabular">₹{p.flightdeals_share}</td><td><Badge variant="outline" className="text-[10px]">{p.status}</Badge></td></tr>))}</tbody></table>
            )}
          </Card>
        </>
      )}
    </Shell>
  );
}

// ---------------- System errors ----------------
export function AdminSystemErrors() {
  const { user, loading, denied, setDenied } = useAdminGuard();
  const [data, setData] = useState(null);
  useEffect(() => { document.title = 'Admin · System | FlightDeals.in'; }, []);
  useEffect(() => { if (!user) return; api.adminSystemErrors().then(({ data }) => setData(data)).catch((e) => { if (e?.response?.status === 403) setDenied(true); }); }, [user, setDenied]);
  if (loading) return <Layout><Spinner /></Layout>;
  if (denied) return <Denied />;
  return (
    <Shell title="System errors" subtitle="Recent API errors from the provider/integration logs." icon={AlertTriangle}>
      {!data ? <Spinner /> : data.errors.length === 0 ? (
        <Card className="p-12 rounded-2xl text-center" data-testid="no-errors"><BadgeCheck className="h-10 w-10 mx-auto text-emerald-500 mb-2" /><p className="font-semibold">No API errors recorded</p><p className="text-sm text-muted-foreground">All systems operating normally.</p></Card>
      ) : (
        <Card className="p-0 rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-errors-table">
            <thead><tr className="text-left text-xs text-muted-foreground border-b border-border"><th className="py-2 px-4">Endpoint</th><th>Message</th><th>When</th></tr></thead>
            <tbody>{data.errors.map((e, i) => (<tr key={i} className="border-b border-border last:border-0"><td className="py-2.5 px-4 font-mono text-xs">{e.endpoint || e.provider || '—'}</td><td className="text-xs text-destructive">{e.error || e.message || 'Error'}</td><td className="text-xs text-muted-foreground">{fmtDate(e.created_at)}</td></tr>))}</tbody>
          </table>
        </Card>
      )}
    </Shell>
  );
}
